import * as yup from 'yup';
import {
  AdminProductDTO,
  CreateProductDTO,
  DiscountType,
  IMAGE_ALLOWED_TYPES,
  IMAGE_MAX_BYTES,
  IMAGE_MAX_PER_PRODUCT,
  PRICE_LEVELS,
  PRODUCT_CODE_PATTERN,
  PRODUCT_MAX_PRICE,
  PriceLevel,
  ProductDiscount,
  UpdateProductDTO,
} from '@src/common-utils/DataModels';

/**
 * Pure helpers for the admin product form (no React): form values <-> API DTOs, validation rules that mirror
 * dental-ware-services (ProductAdminModels.ts), and image list utilities. Numeric inputs are kept as strings in the
 * form and converted here.
 */

// ---------------------------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------------------------

export type DiscountFormValues = {
  enabled: boolean;
  discountType: DiscountType;
  description: string;
  discountValue: string;
  discountYValue: string;
};

export type ProductFormValues = {
  code: string;
  name: string;
  description: string;
  /** one specification entry per line */
  specificationText: string;
  packing: string;
  unit: string;
  listPrice: string;
  priceLevels: Record<PriceLevel, string>;
  discount: DiscountFormValues;
};

export type ExistingImageItem = { kind: 'existing'; id: string; key: string; url: string };
export type NewImageItem = { kind: 'new'; id: string; file: File; previewUrl: string };
export type ProductImageItem = ExistingImageItem | NewImageItem;

// ---------------------------------------------------------------------------------------------
// Specification text <-> list
// ---------------------------------------------------------------------------------------------

export const specificationToArray = (text: string): string[] =>
  text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

export const specificationToText = (specification?: string[] | null): string => (specification ?? []).join('\n');

// ---------------------------------------------------------------------------------------------
// Number validation (strings from inputs)
// ---------------------------------------------------------------------------------------------

const MONEY_PATTERN = /^\d+(\.\d{1,2})?$/;
const POSITIVE_INTEGER_PATTERN = /^[1-9]\d{0,5}$/;

export const isValidMoney = (text: string): boolean => {
  const value = text.trim();
  return MONEY_PATTERN.test(value) && Number(value) <= PRODUCT_MAX_PRICE;
};

/** null = valid */
export const validateDiscountValue = (discountType: DiscountType, text: string): string | null => {
  const value = text.trim();
  if (!value) {
    return 'Value is required';
  }
  if (discountType === 'PERCENTAGE') {
    const percentage = Number(value);
    return MONEY_PATTERN.test(value) && percentage > 0 && percentage <= 100
      ? null
      : 'Enter a percentage greater than 0 and at most 100';
  }
  return POSITIVE_INTEGER_PATTERN.test(value) ? null : 'Enter a whole number of at least 1';
};

/** Y = number of free items (BUY_X_GET_Y_FREE only). Blank is allowed and means 1. */
export const validateDiscountYValue = (discountType: DiscountType, text: string): string | null => {
  const value = text.trim();
  if (!value) {
    return null;
  }
  if (discountType === 'PERCENTAGE') {
    return 'Free items only apply to Buy X Get Y Free';
  }
  return POSITIVE_INTEGER_PATTERN.test(value) ? null : 'Enter a whole number of at least 1';
};

// ---------------------------------------------------------------------------------------------
// Validation schema (react-hook-form + yup)
// ---------------------------------------------------------------------------------------------

// `required` is declared first so that an empty field reports "is required" (react-hook-form shows the first error)
const requiredMoneyField = (label: string) =>
  yup
    .string()
    .required(`${label} is required`)
    .test('money', `${label} must be a number with at most 2 decimals (max ${PRODUCT_MAX_PRICE})`, (value) =>
      isValidMoney(value ?? ''),
    );

export const productFormSchema = (mode: 'CREATE' | 'UPDATE') =>
  yup.object({
    code:
      mode === 'CREATE'
        ? yup
            .string()
            .trim()
            .required('Code is required')
            .matches(PRODUCT_CODE_PATTERN, 'Use 1-32 letters, digits, "-" or "_"')
        : yup.string(),
    name: yup.string().trim().required('Name is required').max(100, 'At most 100 characters'),
    description: yup.string().max(2000, 'At most 2000 characters'),
    specificationText: yup.string().test('specification', 'At most 20 lines, each up to 200 characters', (value) => {
      const lines = specificationToArray(value ?? '');
      return lines.length <= 20 && lines.every((line) => line.length <= 200);
    }),
    packing: yup.string().trim().required('Packing is required').max(100, 'At most 100 characters'),
    unit: yup.string().trim().required('Unit is required').max(20, 'At most 20 characters'),
    listPrice: requiredMoneyField('List price'),
    priceLevels: yup.object(
      Object.fromEntries(
        PRICE_LEVELS.map((level) => [
          level,
          yup
            .string()
            .test(
              'money',
              `Price ${level} must be a number with at most 2 decimals`,
              (value) => !value || isValidMoney(value),
            ),
        ]),
      ),
    ),
    discount: yup.object({
      enabled: yup.boolean().required(),
      discountType: yup.string().oneOf(['BUY_X_GET_Y_FREE', 'PERCENTAGE']).required(),
      description: yup.string().when('enabled', {
        is: true,
        then: (schema) => schema.trim().required('Description is required').max(200, 'At most 200 characters'),
      }),
      discountValue: yup.string().when('enabled', {
        is: true,
        then: (schema) =>
          schema.test('discount-value', (value, context) => {
            const error = validateDiscountValue(context.parent.discountType, value ?? '');
            return error ? context.createError({ message: error }) : true;
          }),
      }),
      discountYValue: yup.string().when('enabled', {
        is: true,
        then: (schema) =>
          schema.test('discount-y-value', (value, context) => {
            const error = validateDiscountYValue(context.parent.discountType, value ?? '');
            return error ? context.createError({ message: error }) : true;
          }),
      }),
    }),
  });

// ---------------------------------------------------------------------------------------------
// Form values <-> DTOs
// ---------------------------------------------------------------------------------------------

export const emptyPriceLevels = (): Record<PriceLevel, string> =>
  Object.fromEntries(PRICE_LEVELS.map((level) => [level, ''])) as Record<PriceLevel, string>;

export const emptyProductFormValues = (): ProductFormValues => ({
  code: '',
  name: '',
  description: '',
  specificationText: '',
  packing: '',
  unit: '',
  listPrice: '',
  priceLevels: emptyPriceLevels(),
  discount: { enabled: false, discountType: 'PERCENTAGE', description: '', discountValue: '', discountYValue: '' },
});

export const productToFormValues = (product: AdminProductDTO): ProductFormValues => ({
  code: product.code,
  name: product.name,
  description: product.description ?? '',
  specificationText: specificationToText(product.specification),
  packing: product.packing,
  unit: product.unit,
  listPrice: String(product.listPrice),
  priceLevels: Object.fromEntries(
    PRICE_LEVELS.map((level) => {
      const price = product.priceLevels?.[level];
      return [level, price === undefined || price === null ? '' : String(price)];
    }),
  ) as Record<PriceLevel, string>,
  discount: product.discount
    ? {
        enabled: true,
        discountType: product.discount.discountType,
        description: product.discount.description,
        discountValue: String(product.discount.discountValue),
        discountYValue:
          product.discount.discountYValue === undefined || product.discount.discountYValue === null
            ? ''
            : String(product.discount.discountYValue),
      }
    : { ...emptyProductFormValues().discount },
});

/** undefined when no level has a price */
export const toPriceLevels = (values: Record<PriceLevel, string>): Partial<Record<PriceLevel, number>> | undefined => {
  const entries = PRICE_LEVELS.filter((level) => values[level] && values[level].trim() !== '').map(
    (level) => [level, Number(values[level].trim())] as const,
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

/** undefined when the discount is switched off */
export const toDiscount = (values: DiscountFormValues): ProductDiscount | undefined => {
  if (!values.enabled) {
    return undefined;
  }
  return {
    description: values.description.trim(),
    discountType: values.discountType,
    discountValue: Number(values.discountValue.trim()),
    ...(values.discountType === 'BUY_X_GET_Y_FREE'
      ? { discountYValue: values.discountYValue.trim() ? Number(values.discountYValue.trim()) : 1 }
      : {}),
  };
};

export const toCreateProductDTO = (values: ProductFormValues, imageKeys: string[]): CreateProductDTO => {
  const priceLevels = toPriceLevels(values.priceLevels);
  const discount = toDiscount(values.discount);
  const description = values.description.trim();

  return {
    code: values.code.trim(),
    name: values.name.trim(),
    ...(description ? { description } : {}),
    specification: specificationToArray(values.specificationText),
    packing: values.packing.trim(),
    unit: values.unit.trim(),
    listPrice: Number(values.listPrice.trim()),
    ...(priceLevels ? { priceLevels } : {}),
    images: imageKeys,
    ...(discount ? { discount } : {}),
  };
};

/** Sends the full editable field set plus the version; null clears description, price levels and discount. */
export const toUpdateProductDTO = (
  values: ProductFormValues,
  imageKeys: string[],
  version: number,
): UpdateProductDTO => ({
  version,
  name: values.name.trim(),
  description: values.description.trim() || null,
  specification: specificationToArray(values.specificationText),
  packing: values.packing.trim(),
  unit: values.unit.trim(),
  listPrice: Number(values.listPrice.trim()),
  priceLevels: toPriceLevels(values.priceLevels) ?? null,
  images: imageKeys,
  discount: toDiscount(values.discount) ?? null,
});

// ---------------------------------------------------------------------------------------------
// Images
// ---------------------------------------------------------------------------------------------

export const fileExtension = (fileName: string): string =>
  fileName.includes('.') ? (fileName.split('.').pop() as string).toLowerCase() : '';

/** null = acceptable */
export const validateImageFile = (file: { name: string; type: string; size: number }): string | null => {
  const extensions = IMAGE_ALLOWED_TYPES[file.type];
  if (!extensions) {
    return `${file.name}: only JPEG, PNG, GIF or WebP images are allowed`;
  }
  if (!extensions.includes(fileExtension(file.name))) {
    return `${file.name}: the file extension does not match the image type`;
  }
  if (file.size < 1) {
    return `${file.name}: the file is empty`;
  }
  if (file.size > IMAGE_MAX_BYTES) {
    return `${file.name}: images must be ${IMAGE_MAX_BYTES / (1024 * 1024)} MB or smaller`;
  }
  return null;
};

/** null = acceptable */
export const validateImageCount = (currentCount: number, addingCount: number): string | null =>
  currentCount + addingCount > IMAGE_MAX_PER_PRODUCT
    ? `A product can have at most ${IMAGE_MAX_PER_PRODUCT} images`
    : null;

export const moveImage = <T>(list: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return list;
  }
  const copy = [...list];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
};

export const newImageItems = (items: ProductImageItem[]): NewImageItem[] =>
  items.filter((item): item is NewImageItem => item.kind === 'new');

/** The ordered key list to send to the API; new images are replaced by the key returned when they were uploaded. */
export const finalImageKeys = (items: ProductImageItem[], uploadedKeys: Record<string, string>): string[] =>
  items.map((item) => {
    if (item.kind === 'existing') {
      return item.key;
    }
    const key = uploadedKeys[item.id];
    if (!key) {
      throw new Error(`Image was not uploaded: ${item.file.name}`);
    }
    return key;
  });
